// Platform Kit Integration for Admin Panel
// Based on Supabase Platform Kit patterns and workspace rules

import React, { useEffect, useState } from 'react';
// import { useList, useUpdate, useCreate, useDelete } from '@refinedev/core';
import { useToast } from '../hooks/useToast';
import { supabase } from '../lib/supabase';

interface PlatformKitManagerProps {
	projectRef: string;
	onClose?: () => void;
}

interface DatabaseStats {
	total_projects: number;
	projects_by_type: Record<string, number>;
	recent_projects: Array<{
		id: string;
		title: string;
		created_at: string;
	}>;
}

interface MediaStats {
	total_files: number;
	total_size: number;
	files_by_type: Record<string, number>;
	storage_buckets: Record<string, number>;
}

export const PlatformKitManager: React.FC<PlatformKitManagerProps> = ({
	projectRef,
	onClose,
}) => {
	const [activeTab, setActiveTab] = useState<
		'overview' | 'database' | 'storage' | 'realtime'
	>('overview');
	const [databaseStats, setDatabaseStats] = useState<DatabaseStats | null>(
		null
	);
	const [mediaStats, setMediaStats] = useState<MediaStats | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { addToast } = useToast();

	// Load statistics on mount
	useEffect(() => {
		loadStatistics();
	}, []);

	const loadStatistics = async () => {
		setIsLoading(true);
		try {
			// Try RPC first; if it fails, fall back to client-side aggregation
			const { data: dbStats, error: dbError } = await supabase.rpc(
				'get_project_statistics'
			);

			if (!dbError && dbStats && dbStats.length > 0) {
				setDatabaseStats(dbStats[0]);
			} else {
				// Fallback: compute stats client-side
				const { data: projects, error: projError } = await supabase
					.from('projects')
					.select('id,title,created_at,type')
					.order('created_at', { ascending: false });

				if (projError) {
					throw new Error(
						`Database stats failed: ${dbError?.message || projError.message}`
					);
				}

				const total = projects?.length || 0;
				const byType: Record<string, number> = {};
				for (const p of projects || []) {
					const key = (p as any).type || 'No Type';
					byType[key] = (byType[key] || 0) + 1;
				}
				const thirtyDaysAgo = new Date();
				thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
				const recent = (projects || [])
					.filter(
						(p: any) => p.created_at && new Date(p.created_at) > thirtyDaysAgo
					)
					.slice(0, 50)
					.map((p: any) => ({
						id: p.id,
						title: p.title,
						created_at: p.created_at,
					}));

				setDatabaseStats({
					total_projects: total,
					projects_by_type: byType,
					recent_projects: recent,
				});
			}

			// Load media statistics (with fallback)
			const { data: mediaStatsData, error: mediaError } = await supabase.rpc(
				'get_media_usage_stats'
			);

			if (!mediaError && mediaStatsData && mediaStatsData.length > 0) {
				setMediaStats(mediaStatsData[0]);
			} else {
				const { data: files, error: filesError } = await supabase
					.from('media_files')
					.select('file_size,mime_type,folder_path,metadata');

				if (filesError) {
					throw new Error(
						`Media stats failed: ${mediaError?.message || filesError.message}`
					);
				}

				const total_files = files?.length || 0;
				const total_size = (files || []).reduce(
					(sum: number, f: any) => sum + (Number(f.file_size) || 0),
					0
				);
				const files_by_type: Record<string, number> = {};
				const storage_buckets: Record<string, number> = {};
				for (const f of files || []) {
					const type = (f.mime_type || '').split('/')[0] || 'unknown';
					files_by_type[type] = (files_by_type[type] || 0) + 1;
					const bucket =
						(typeof f.metadata === 'object' &&
							f.metadata &&
							(f.metadata as any).bucket) ||
						f.folder_path ||
						'unknown';
					storage_buckets[bucket] = (storage_buckets[bucket] || 0) + 1;
				}

				setMediaStats({
					total_files,
					total_size,
					files_by_type,
					storage_buckets,
				});
			}
		} catch (error) {
			console.error('Failed to load statistics:', error);
			addToast({
				type: 'error',
				message: `Failed to load statistics: ${
					error instanceof Error ? error.message : String(error)
				}`,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const performMediaSync = async () => {
		setIsLoading(true);
		try {
			// Use Supabase client to invoke Edge Function (works in cloud & local dev with CLI)
			const { data: result, error: fnError } = await supabase.functions.invoke(
				'media-management',
				{
					body: { action: 'sync', options: { limit: 1000 } },
				}
			);

			if (fnError) {
				throw new Error(fnError.message);
			}

			if (result.success) {
				addToast({
					type: 'success',
					message: `Media sync completed: ${result.data.message}`,
				});

				// Reload statistics
				await loadStatistics();
			} else {
				throw new Error(result.error || 'Sync failed');
			}
		} catch (error) {
			console.error('Media sync failed:', error);
			addToast({
				type: 'error',
				message: `Media sync failed: ${
					error instanceof Error ? error.message : String(error)
				}`,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const validateMediaIntegrity = async () => {
		setIsLoading(true);
		try {
			const { data: result, error: fnError } = await supabase.functions.invoke(
				'media-management',
				{
					body: { action: 'validate' },
				}
			);

			if (fnError) {
				throw new Error(fnError.message);
			}

			if (result.success) {
				addToast({
					type: result.data.issues_count > 0 ? 'warning' : 'success',
					message: `Validation completed: ${result.data.message}`,
				});
			} else {
				throw new Error(result.error || 'Validation failed');
			}
		} catch (error) {
			console.error('Media validation failed:', error);
			addToast({
				type: 'error',
				message: `Media validation failed: ${
					error instanceof Error ? error.message : String(error)
				}`,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	return (
		<div className='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4'>
			<div className='relative w-[1100px] h-[80vh] bg-white rounded-lg overflow-hidden flex flex-col'>
				{/* Header */}
				<div className='p-6 border-b border-gray-200 flex justify-between items-center'>
					<h2 className='text-2xl font-semibold text-gray-900'>
						Platform Management
					</h2>
					{onClose && (
						<button
							onClick={onClose}
							className='p-2 rounded-full hover:bg-gray-100 transition-colors'>
							<svg
								className='w-6 h-6'
								fill='none'
								stroke='currentColor'
								viewBox='0 0 24 24'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M6 18L18 6M6 6l12 12'
								/>
							</svg>
						</button>
					)}
				</div>

				{/* Tab Navigation */}
				<div className='border-b border-gray-200'>
					<nav className='flex space-x-8 px-6'>
						{[
							{ id: 'overview', label: 'Overview' },
							{ id: 'database', label: 'Database' },
							{ id: 'storage', label: 'Storage' },
							{ id: 'realtime', label: 'Realtime' },
						].map((tab) => (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id as typeof activeTab)}
								className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
									activeTab === tab.id
										? 'border-blue-500 text-blue-600'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
								}`}>
								{tab.label}
							</button>
						))}
					</nav>
				</div>

				{/* Content Area */}
				<div className='p-6 flex-1 overflow-y-auto'>
					{activeTab === 'overview' && (
						<div className='space-y-6'>
							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
								{/* Project Stats */}
								<div className='bg-blue-50 p-4 rounded-lg'>
									<h3 className='text-sm font-medium text-blue-600'>
										Total Projects
									</h3>
									<p className='text-2xl font-bold text-blue-900'>
										{databaseStats?.total_projects || 0}
									</p>
								</div>

								{/* Media Stats */}
								<div className='bg-green-50 p-4 rounded-lg'>
									<h3 className='text-sm font-medium text-green-600'>
										Media Files
									</h3>
									<p className='text-2xl font-bold text-green-900'>
										{mediaStats?.total_files || 0}
									</p>
								</div>

								<div className='bg-purple-50 p-4 rounded-lg'>
									<h3 className='text-sm font-medium text-purple-600'>
										Storage Used
									</h3>
									<p className='text-2xl font-bold text-purple-900'>
										{formatFileSize(mediaStats?.total_size || 0)}
									</p>
								</div>

								<div className='bg-orange-50 p-4 rounded-lg'>
									<h3 className='text-sm font-medium text-orange-600'>
										Project Ref
									</h3>
									<p className='text-sm font-mono text-orange-900 truncate'>
										{projectRef}
									</p>
								</div>
							</div>

							{/* Recent Projects */}
							{databaseStats?.recent_projects &&
								databaseStats.recent_projects.length > 0 && (
									<div>
										<h3 className='text-lg font-medium text-gray-900 mb-4'>
											Recent Projects
										</h3>
										<div className='space-y-2'>
											{databaseStats.recent_projects
												.slice(0, 5)
												.map((project) => (
													<div
														key={project.id}
														className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'>
														<span className='font-medium'>{project.title}</span>
														<span className='text-sm text-gray-500'>
															{new Date(
																project.created_at
															).toLocaleDateString()}
														</span>
													</div>
												))}
										</div>
									</div>
								)}
						</div>
					)}

					{activeTab === 'database' && (
						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900'>
								Database Management
							</h3>

							{/* Project Types Distribution */}
							{databaseStats?.projects_by_type && (
								<div>
									<h4 className='text-md font-medium text-gray-700 mb-3'>
										Projects by Type
									</h4>
									<div className='grid grid-cols-2 gap-3'>
										{Object.entries(databaseStats.projects_by_type).map(
											([type, count]) => (
												<div
													key={type}
													className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'>
													<span className='capitalize'>{type}</span>
													<span className='font-bold'>{count}</span>
												</div>
											)
										)}
									</div>
								</div>
							)}

							{/* Database Actions */}
							<div className='space-y-3'>
								<h4 className='text-md font-medium text-gray-700'>
									Database Actions
								</h4>
								<button
									onClick={loadStatistics}
									disabled={isLoading}
									className='w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors'>
									{isLoading ? 'Refreshing...' : 'Refresh Statistics'}
								</button>
							</div>
						</div>
					)}

					{activeTab === 'storage' && (
						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900'>
								Storage Management
							</h3>

							{/* Storage Stats */}
							{mediaStats && (
								<div className='space-y-4'>
									{/* Files by Type */}
									<div>
										<h4 className='text-md font-medium text-gray-700 mb-3'>
											Files by Type
										</h4>
										<div className='grid grid-cols-2 gap-3'>
											{Object.entries(mediaStats.files_by_type).map(
												([type, count]) => (
													<div
														key={type}
														className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'>
														<span className='capitalize'>{type}</span>
														<span className='font-bold'>{count}</span>
													</div>
												)
											)}
										</div>
									</div>

									{/* Storage Buckets */}
									<div>
										<h4 className='text-md font-medium text-gray-700 mb-3'>
											Storage Buckets
										</h4>
										<div className='grid grid-cols-1 gap-3'>
											{Object.entries(mediaStats.storage_buckets).map(
												([bucket, count]) => (
													<div
														key={bucket}
														className='flex justify-between items-center p-3 bg-gray-50 rounded-lg'>
														<span className='font-mono'>{bucket}</span>
														<span className='font-bold'>{count} files</span>
													</div>
												)
											)}
										</div>
									</div>
								</div>
							)}

							{/* Storage Actions */}
							<div className='space-y-3'>
								<h4 className='text-md font-medium text-gray-700'>
									Storage Actions
								</h4>
								<button
									onClick={performMediaSync}
									disabled={isLoading}
									className='w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors'>
									{isLoading ? 'Syncing...' : 'Sync Media Files'}
								</button>
								<button
									onClick={validateMediaIntegrity}
									disabled={isLoading}
									className='w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors'>
									{isLoading ? 'Validating...' : 'Validate Media Integrity'}
								</button>
							</div>
						</div>
					)}

					{activeTab === 'realtime' && (
						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900'>
								Realtime Management
							</h3>

							<div className='space-y-4'>
								<div className='p-4 bg-green-50 rounded-lg'>
									<h4 className='font-medium text-green-800'>
										Realtime Status
									</h4>
									<p className='text-green-600'>
										✅ Realtime broadcasts are enabled
									</p>
									<p className='text-sm text-green-500 mt-1'>
										Projects, media files, and categories will broadcast changes
										automatically
									</p>
								</div>

								<div className='p-4 bg-blue-50 rounded-lg'>
									<h4 className='font-medium text-blue-800'>
										Available Channels
									</h4>
									<ul className='text-blue-600 text-sm space-y-1 mt-2'>
										<li>
											• <code>project:&lt;id&gt;</code> - Project-specific
											updates
										</li>
										<li>
											• <code>admin:projects</code> - Admin project management
										</li>
										<li>
											• <code>media:&lt;bucket&gt;:&lt;folder&gt;</code> - Media
											file updates
										</li>
										<li>
											• <code>admin:media</code> - Admin media management
										</li>
										<li>
											• <code>categories:changes</code> - Category updates
										</li>
									</ul>
								</div>

								<div className='p-4 bg-yellow-50 rounded-lg'>
									<h4 className='font-medium text-yellow-800'>Security</h4>
									<p className='text-yellow-600 text-sm'>
										All realtime channels use private mode with RLS policies for
										secure access
									</p>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className='p-6 border-t border-gray-200 bg-gray-50'>
					<div className='flex justify-between items-center text-sm text-gray-500'>
						<span>Platform Kit Integration • Enhanced Supabase Management</span>
						<span>Last updated: {new Date().toLocaleTimeString()}</span>
					</div>
				</div>
			</div>
		</div>
	);
};
